import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetprovidersListForFilters } from "../../custom-hooks/queries";
import TAIndicationPivot from "../../components/TAIndicationChart";
import { useSelector } from "react-redux";
import { VendorFilters } from "../../components/Provider/VendorFilters";
import { debounce } from "lodash";

const INDICATION_EXPERIENCE_TAB_NAME =
  "Therapeutic Area & Indication Experience";

const ProviderCompare = () => {
  const providerIds = useSelector(
    (state) => state.indicationsDashboardFilters.providerIds
  );

  const { data: vendorsFilters } = useGetprovidersListForFilters();
  const allAvailableVendors = useMemo(
    () =>
      vendorsFilters?.map((p) => ({
        label: p.name,
        value: p.id,
        dotColorClass: "bg-green-400",
      })) ?? [],
    [vendorsFilters]
  );

  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedTAIndications, setSelectedTAIndications] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedVendorType, setSelectedVendorType] = useState([]);
  const [selectedEmployeeSizes, setSelectedEmployeeSizes] = useState([]);
  const [selectedDocAvailability, setSelectedDocAvailability] = useState([]);
  const [selectedDataAvailability, setSelectedDataAvailability] = useState([]);
  const [selectedRevenueRanges, setSelectedRevenueRanges] = useState([]);
  const [selectedLocationFilters, setSelectedLocationFilters] = useState([]);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam || INDICATION_EXPERIENCE_TAB_NAME;
  const [taTipsExpanded, setTaTipsExpanded] = useState(false);

  const [activeTab, setActiveTab] = useState(initialTab);

  const navigate = useNavigate();

  useEffect(() => {
    setSelectedVendors(
      allAvailableVendors?.filter((vendor) =>
        providerIds.includes(vendor.value)
      ) || []
    );
  }, [providerIds, allAvailableVendors]);

  const onVendorFilterChange = useCallback(
    debounce((filters) => {
      setSelectedVendors(filters.selectedVendors);
      setSelectedTAIndications(filters.selectedTAIndications);
      setSelectedCategories(filters.selectedCategories);
      setSelectedVendorType(filters.selectedVendorType);
      setSelectedDocAvailability(filters.selectedDocAvail);
      setSelectedDataAvailability(filters.selectedDataAvailability);
      setSelectedEmployeeSizes(filters.selectedEmployeeSizes);
      setSelectedRevenueRanges(filters.selectedRevenueRanges);
      setSelectedLocationFilters(filters.locationFilters);
    }, 300),
    []
  );

  return (
    <div className={`container bg-gray-50 min-h-screen`}>
      <header className="px-5 bg-white py-2 pb-4 flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 lg:space-x-4 mb-6 shadow-header">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Clinical Experience
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Select a set of vendors and service categories to compare risk
            scoring information.
          </p>
        </div>
        <div className="w-full lg:w-1/2 mt-5 flex justify-center items-center lg:justify-end">
          <CustomButton
            color={"bg-indigo-600"}
            size={"text-base"}
            weight={"font-normal"}
            height={"h-9"}
            width={"w-[110px]"}
            textColor={"text-white"}
            text={"View Vendors"}
            classes={
              "block rounded-md text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            }
            onClick={() => {
              navigate("/Providers");
            }}
          />
        </div>
      </header>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="space-y-8 py-4">
          <VendorFilters
            defaultSelectedVendors={selectedVendors}
            onFilterChange={onVendorFilterChange}
          />

          {/* Tabs Section - No background */}
          <div>
            {activeTab === INDICATION_EXPERIENCE_TAB_NAME && (
              <div>
                {/* Grid Usage Instructions */}
                <div className="mt-6 mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div
                    className="flex items-start gap-3 cursor-pointer -m-4 p-4"
                    onClick={() => setTaTipsExpanded(!taTipsExpanded)}
                  >
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-indigo-600 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l-.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </div>
                    <h4 className="text-sm font-semibold text-indigo-900">
                      Quick Grid Tips
                    </h4>
                    <svg
                      className={`h-4 w-4 text-indigo-600 transition-transform duration-250 ease-out mt-0.5 ${
                        taTipsExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m19.5 8.25-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-250 ease-out ${
                      taTipsExpanded ? "max-h-48 pt-4" : "max-h-0 pt-0"
                    }`}
                  >
                    <ul className="text-sm text-indigo-800 space-y-1 pl-8">
                      <li>
                        •{" "}
                        <span className="font-medium">
                          Drag column headers to the pivot area
                        </span>{" "}
                        to group data differently
                      </li>
                      <li>
                        •{" "}
                        <span className="font-medium">
                          Use the Columns panel (right)
                        </span>{" "}
                        to show/hide specific therapeutic areas or indications
                      </li>
                      <li>
                        •{" "}
                        <span className="font-medium">
                          Right-click on any cell
                        </span>{" "}
                        for export and analysis options
                      </li>
                      <li>
                        • <span className="font-medium">Sort any column</span>{" "}
                        by clicking the header
                      </li>
                      <li>
                        • <span className="font-medium">Use Filters panel</span>{" "}
                        to focus on specific vendors
                      </li>
                    </ul>
                  </div>
                </div>

                <TAIndicationPivot
                  selectedVendors={selectedVendors}
                  selectedVendorType={selectedVendorType}
                  selectedCategories={selectedCategories}
                  selectedTAIndications={selectedTAIndications}
                  selectedEmployeeSizes={selectedEmployeeSizes}
                  selectedDocAvailability={selectedDocAvailability}
                  selectedDataAvailability={selectedDataAvailability}
                  selectedRevenueRanges={selectedRevenueRanges}
                  selectedLocationFilters={selectedLocationFilters}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderCompare;
